import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AreaChart, Area, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import s from './NovaBroker.module.sass';
import { useAppContext } from '../../../app-core/AppContext';
import BankLogo from '../components/BankLogo';
import FlagIcon from '../../../components/FlagIcon/FlagIcon';
import {
    UserPlus, Building2, ClipboardList, SlidersHorizontal,
    Users, Percent, Send, ArrowRight, TrendingUp,
    Check, Clock, X, Eye, EyeOff, CalendarDays,
    User, Landmark, CreditCard, BadgeCheck, LineChart as LineChartIcon, Copy,
    FileBarChart2, PieChart, Layers, KeyRound, ShieldCheck, ChevronDown,
} from 'lucide-react';
import bitcoin from '../../../img/Bitcoin.svg.png';
import etherium from '../../../img/Ethereum-ETH-icon.png';
import binance from '../../../img/binance-coin-bnb-logo-CD94CC6D31-seeklogo.com.png';
import usdclogo from '../../../img/usdclogo.png';
import tron from '../../../img/tron.png';

// Лоадер/бренд-режим: включается только при запуске через start-branded.command
// (лаунчер выставляет REACT_APP_BRANDED=1).
const BRANDED = process.env.REACT_APP_BRANDED === '1';

// Путь к аватару из настроек сайта (defaultUserAvatar): абсолютный/URL — как есть,
// иначе через /data/avatar/ (так же, как NovaHeader — это надёжный путь).
const resolveAvatarSrc = (a) => {
    if (!a) return null;
    if (a.startsWith('/') || a.startsWith('http')) return a;
    return `/data/avatar/${encodeURIComponent(a)}`;
};

// Аватар клиента для branded-строки: пробует картинку из настроек, при ошибке
// загрузки аккуратно откатывается на инициалы (кружок не остаётся пустым).
function BrandAvatar({ src, initials, gradient }) {
    const [failed, setFailed] = useState(false);
    return (
        <span className={s.clientAva} style={{ background: gradient }} aria-hidden="true">
            {src && !failed
                ? <img className={s.clientAvaImg} src={src} alt="" onError={() => setFailed(true)} />
                : initials}
        </span>
    );
}

// Активы для распределения дохода: иконка + фирменный цвет монеты
const COIN_META = {
    BTC: { icon: bitcoin, color: '#F7931A' },
    ETH: { icon: etherium, color: '#627EEA' },
    BNB: { icon: binance, color: '#F0B90B' },
    USDC: { icon: usdclogo, color: '#2775CA' },
    USDT: { icon: tron, color: '#26A17B' },
};

// ── Генераторы данных (логика оригинального BrokerProgram)

const getRandomUSDT = () => Math.floor(Math.random() * 100) + 1;
const getRandomDigits = () => Math.floor(1000 + Math.random() * 9000);

const generateTxId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'TX';
    for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
};

const getRandomAmount = (formatFn, siteSettings) => {
    const ranges = siteSettings?.brokerAmountGeneration?.ranges || [
        { id: 'default', name: 'По умолчанию', min: 1257932.02, max: 12284583.71, weight: 100 },
    ];
    const weightedRanges = [];
    ranges.forEach(range => {
        for (let i = 0; i < range.weight; i++) weightedRanges.push(range);
    });
    const selectedRange = weightedRanges[Math.floor(Math.random() * weightedRanges.length)];
    const randomAmount = selectedRange.min + (Math.random() * (selectedRange.max - selectedRange.min));
    if (formatFn) return formatFn(randomAmount);
    const parts = randomAmount.toFixed(2).split('.');
    return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${parts[1]}`;
};

// Свободный парсер суммы: понимает «3482847», «3.482.847,29», «2,979,519.53».
// Если разделитель один и после него 1–2 цифры — это десятичная часть,
// иначе — разделитель тысяч. Возвращает число или NaN.
const parseAmountLoose = (raw) => {
    const s0 = String(raw == null ? '' : raw).replace(/[^\d.,-]/g, '');
    if (!s0) return NaN;
    const lastDot = s0.lastIndexOf('.');
    const lastComma = s0.lastIndexOf(',');
    let dec = '';
    if (lastDot !== -1 && lastComma !== -1) {
        dec = lastDot > lastComma ? '.' : ',';
    } else if (lastDot !== -1 || lastComma !== -1) {
        const sep = lastDot !== -1 ? '.' : ',';
        const idx = lastDot !== -1 ? lastDot : lastComma;
        const digitsAfter = s0.length - idx - 1;
        const count = s0.split(sep).length - 1;
        dec = (count === 1 && digitsAfter > 0 && digitsAfter <= 2) ? sep : '';
    }
    let intPart = s0;
    let frac = '0';
    if (dec) {
        const i = s0.lastIndexOf(dec);
        intPart = s0.slice(0, i);
        frac = s0.slice(i + 1).replace(/\D/g, '') || '0';
    }
    return parseFloat(`${intPart.replace(/[^\d-]/g, '') || '0'}.${frac}`);
};

// Случайный банк ТОЛЬКО из включённых в настройках
const getRandomNetwork = (siteSettings) => {
    if (!siteSettings?.banks) return 'Default Bank';
    const enabledBanks = Object.keys(siteSettings.banks).filter(
        bankName => siteSettings.banks[bankName]?.enabled === true
    );
    if (enabledBanks.length === 0) return 'No Banks Enabled';
    return enabledBanks[Math.floor(Math.random() * enabledBanks.length)];
};

const getBankIcon = (bankName, siteSettings) => {
    const bankSettings = siteSettings?.banks?.[bankName];
    if (bankSettings?.iconFileName) return `/bankicons/${bankSettings.iconFileName}`;
    return null;
};

// График P&L: две пересекающиеся растущие линии (логика оригинала)
const generateRandomChartData = (dailyPnLEnd, accumulatedPnLEnd) => {
    const data = [];
    const numPoints = 10;
    const greenStart = 8;
    const greenEnd = dailyPnLEnd || 22;
    const orangeStart = 3;
    const orangeEnd = accumulatedPnLEnd || 18;

    const greenTempData = [];
    let greenTemp = greenStart;
    for (let i = 0; i < numPoints; i++) {
        greenTemp += (Math.random() * 10) - 4;
        if (greenTemp < 2) greenTemp = 2;
        greenTempData.push(greenTemp);
    }
    const greenMin = Math.min(...greenTempData);
    const greenMax = Math.max(...greenTempData);
    const greenRange = greenMax - greenMin;
    const greenTargetRange = greenEnd - greenStart;

    const orangeTempData = [];
    let orangeTemp = orangeStart;
    for (let i = 0; i < numPoints; i++) {
        orangeTemp += (Math.random() * 3) - 1;
        if (orangeTemp < orangeStart * 0.8) orangeTemp = orangeStart * 0.8;
        orangeTempData.push(orangeTemp);
    }
    const orangeMin = Math.min(...orangeTempData);
    const orangeMax = Math.max(...orangeTempData);
    const orangeRange = orangeMax - orangeMin;
    const orangeTargetRange = orangeEnd - orangeStart;

    for (let i = 0; i < numPoints; i++) {
        const greenNormalized = greenRange > 0 ? (greenTempData[i] - greenMin) / greenRange : 0;
        const orangeNormalized = orangeRange > 0 ? (orangeTempData[i] - orangeMin) / orangeRange : 0;
        data.push({
            name: '',
            greenValue: parseFloat((greenStart + greenNormalized * greenTargetRange).toFixed(2)),
            yellowValue: parseFloat((orangeStart + orangeNormalized * orangeTargetRange).toFixed(2)),
            chartData: null,
        });
    }
    if (data.length > 0) {
        data[0].greenValue = greenStart;
        data[data.length - 1].greenValue = greenEnd;
        data[0].yellowValue = orangeStart;
        data[data.length - 1].yellowValue = orangeEnd;
    }
    data.forEach(point => { point.chartData = data; });
    return data;
};

// ── Аватар клиента: инициалы на детерминированном градиенте из имени
const clientInitials = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '·';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Широкая палитра градиентов: 24 пары по всему цветовому кругу, каждая
// с меткой «семейства» оттенка — чтобы похожие цвета не шли подряд
const AVA_GRADIENTS = [
    { c: ['#E0533D', '#A83226'], f: 'red' },      // терракота
    { c: ['#D4425A', '#9B2A3D'], f: 'red' },      // малиновый
    { c: ['#C05A38', '#8A3A20'], f: 'orange' },   // ржавый
    { c: ['#E06B2D', '#9E4A16'], f: 'orange' },   // оранжевый
    { c: ['#DB8748', '#A05A20'], f: 'orange' },   // абрикос
    { c: ['#C98A22', '#8F6210'], f: 'amber' },    // охра
    { c: ['#8FA31E', '#5F7212'], f: 'olive' },    // олива
    { c: ['#6E8F2A', '#4A6317'], f: 'olive' },    // хаки
    { c: ['#3E9E4F', '#256B33'], f: 'green' },    // зелёный
    { c: ['#2E8547', '#1C5A2F'], f: 'green' },    // лесной
    { c: ['#2BA47E', '#1A6E54'], f: 'teal' },     // изумруд
    { c: ['#25A0A8', '#166B72'], f: 'cyan' },     // бирюза
    { c: ['#2A7F8F', '#1A5560'], f: 'cyan' },     // петроль
    { c: ['#2E8FC7', '#1C5F8C'], f: 'azure' },    // голубой
    { c: ['#3F6FD8', '#2747A0'], f: 'blue' },     // синий
    { c: ['#6A5AE0', '#4638A8'], f: 'indigo' },   // индиго
    { c: ['#8E4FD1', '#63339A'], f: 'violet' },   // фиолетовый
    { c: ['#B84AC0', '#83308A'], f: 'magenta' },  // пурпур
    { c: ['#D14A8E', '#992E64'], f: 'pink' },     // маджента
    { c: ['#9A5A86', '#6C3C5D'], f: 'plum' },     // слива
    { c: ['#A9714B', '#77492B'], f: 'brown' },    // коричневый
    { c: ['#7A6A55', '#544838'], f: 'taupe' },    // тауп
    { c: ['#5C7A8A', '#3D5561'], f: 'slate' },    // серо-синий
    { c: ['#647092', '#454E68'], f: 'steel' },    // стальной
];

// djb2: стабильный хэш с хорошим распределением по палитре
const hashName = (name) => {
    const str = String(name || '');
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return Math.abs(h);
};

// Подбор градиента: базовый цвет — из хэша имени, но если его семейство
// совпадает с двумя предыдущими аватарами в ленте, пробуем следующие
// (шаг 7 взаимно прост с 24 — обойдём всю палитру без повторов)
const pickClientGradient = (name, recentFamilies) => {
    const base = hashName(name) % AVA_GRADIENTS.length;
    let chosen = AVA_GRADIENTS[base];
    for (let k = 0; k < AVA_GRADIENTS.length; k++) {
        const candidate = AVA_GRADIENTS[(base + k * 7) % AVA_GRADIENTS.length];
        if (!recentFamilies.includes(candidate.f)) {
            chosen = candidate;
            break;
        }
    }
    recentFamilies.push(chosen.f);
    if (recentFamilies.length > 2) recentFamilies.shift();
    return `linear-gradient(135deg, ${chosen.c[0]}, ${chosen.c[1]})`;
};

export default function NovaBroker() {
    const {
        siteSettings = {},
        selectedCrypto,
        pnlDate,
        setPnlDate,
        setPnlDateUserEdited,
        safeT,
        // Для branded-режима: банк/счёт из админки, имя/аватар из настроек
        network,
        adressCrypto,
        imgCoin,
        // «Показать в бинпале»: данные текущего юзера в верхнюю строку
        showInBinpanel,
        nameUser,
        confirmedNameUser,
        maxTotalMoney,
        cryptoAdress,
    } = useAppContext();

    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const currencyFormat = siteSettings.currencyFormat || 'USD';

    const totalElems = 5000;
    const linesOnPage = 200;
    const [currentPage] = useState(1);
    const [statuses, setStatuses] = useState([]);
    const [randomData, setRandomData] = useState([]);
    const [namesAssigned, setNamesAssigned] = useState([]);
    const [allNames, setAllNames] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [clientsCount, setClientsCount] = useState(49217);
    const [isEditingDate, setIsEditingDate] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalClosing, setIsModalClosing] = useState(false);
    const [isAccountVisible, setIsAccountVisible] = useState(false);
    const [txIdCopied, setTxIdCopied] = useState(false);
    const [countedAmount, setCountedAmount] = useState('');

    // Одноразовое редактирование строки ленты (включается настройкой конфига
    // brokerEditableNames). Можно править имя, банк (дропдаун из конфига),
    // последние 4 цифры счёта, сумму, а также время и хэш (TX ID).
    // Переопределения хранятся по globalIndex и нигде не сохраняются — только
    // на время сессии до перегенерации данных.
    const namesEditable = !!siteSettings?.brokerEditableNames;
    // Дерево подполей: что именно можно править (по умолчанию всё включено).
    // Внутреннее поле банка называется 'network', в настройках — ключ 'bank'.
    const editFields = siteSettings?.brokerEditableFields || {};
    const fieldKeyFor = (f) => (f === 'network' ? 'bank' : f);
    const canEditField = (f) => namesEditable && editFields[fieldKeyFor(f)] !== false;
    const [editedRows, setEditedRows] = useState({}); // { [contentIdx]: { name, network, digits, amount, txId } }
    const [editing, setEditing] = useState(null);      // { index, field } | null
    const [editingValue, setEditingValue] = useState('');
    // Синхронный флаг «идёт правка» — чтобы клик вне поля не открывал карточку
    const editingRef = useRef(false);
    // Выделение текста мышкой часто заканчивается ЗА краем поля: браузер шлёт
    // click в строку, и редактор закрывался. Запоминаем, где начался mousedown:
    // если внутри редактора — click по строке игнорируем (как в модалках).
    const editBoxRef = useRef(null);
    const pressStartedInEditorRef = useRef(false);
    useEffect(() => {
        if (!editing) return undefined;
        const onDown = (e) => {
            pressStartedInEditorRef.current = !!(editBoxRef.current && editBoxRef.current.contains(e.target));
        };
        document.addEventListener('mousedown', onDown, true);
        return () => document.removeEventListener('mousedown', onDown, true);
    }, [editing]);

    // ── Перетаскивание строк ленты: КОНТЕНТ (имя, банк, сумма, цифры, хэш,
    // статус, правки) двигается вместе со строкой, а ВРЕМЯ закреплено за
    // позицией в списке. rowOrder[позиция] = индекс контента в currentData
    // (identity после генерации). Правки времени — отдельно, по позициям.
    const [rowOrder, setRowOrder] = useState([]);
    const [timeOverrides, setTimeOverrides] = useState({}); // { [pos]: Date }
    const rowRefs = useRef([]);
    const dropGhostRef = useRef(null);
    // Блокирует клик по строке сразу после дропа, чтобы не открывалась карточка
    const suppressRowClickRef = useRef(false);

    // 24-часовое HH:MM из даты — стабильный префилл для правки времени
    const toEditableTime = (d) => {
        const x = new Date(d);
        return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
    };

    const startEdit = (globalIndex, field, currentValue, e) => {
        if (!canEditField(field)) return;
        e?.stopPropagation();
        editingRef.current = true;
        setEditingValue(currentValue == null ? '' : String(currentValue));
        setEditing({ index: globalIndex, field });
    };
    const cancelEdit = () => { editingRef.current = false; setEditing(null); setEditingValue(''); };
    const commitEdit = (baseDate) => {
        if (!editing) return;
        const { index, field } = editing;
        const raw = editingValue;
        if (field === 'time') {
            // Время принадлежит ПОЗИЦИИ (index = позиция в списке), а не контенту:
            // при перетаскивании строк время остаётся на месте
            const m = raw.match(/(\d{1,2})\D+(\d{1,2})/);
            if (m) {
                const h = Math.min(23, parseInt(m[1], 10));
                const mi = Math.min(59, parseInt(m[2], 10));
                const d = new Date(baseDate);
                d.setHours(h, mi, 0, 0);
                setTimeOverrides(prev => ({ ...prev, [index]: d }));
            }
        } else {
            setEditedRows(prev => {
                const cur = { ...(prev[index] || {}) };
                if (field === 'name') { const v = raw.trim(); if (v) cur.name = v; }
                else if (field === 'amount') {
                    // Введённое значение прогоняем через общий форматтер
                    // (точки/запятые по настройке numberFormat конфига)
                    const n = parseAmountLoose(raw);
                    if (!isNaN(n)) cur.amount = formatCurrency(n);
                    else if (raw.trim()) cur.amount = raw.trim();
                }
                else if (field === 'txId') { const v = raw.replace(/^#/, '').trim(); if (v) cur.txId = v; }
                else if (field === 'digits') { const v = raw.replace(/\D/g, '').slice(0, 4); if (v) cur.digits = v; }
                return { ...prev, [index]: cur };
            });
        }
        editingRef.current = false;
        setEditing(null);
        setEditingValue('');
    };
    // Банк выбирается из кастомного дропдауна — коммитим сразу
    const commitBank = (globalIndex, bankValue) => {
        setEditedRows(prev => ({ ...prev, [globalIndex]: { ...(prev[globalIndex] || {}), network: bankValue } }));
        editingRef.current = false;
        setEditing(null);
    };
    const isEditingCell = (globalIndex, field) => editing && editing.index === globalIndex && editing.field === field;

    // ── Перетаскивание строки за невидимую зону слева от аватарки.
    // Pointer-события вместо HTML5 dnd: строка следует за курсором (transform,
    // без ре-рендеров), соседи расступаются, пунктирный «призрак» показывает
    // слот. На дропе контент переезжает (splice в rowOrder), время остаётся.
    const handleRowDragStart = (e, fromPos, rowsCount) => {
        if (e.button !== 0) return;
        if (e.pointerType && e.pointerType !== 'mouse') return;
        e.stopPropagation();
        const el = rowRefs.current[fromPos];
        if (!el) return;
        const rows = rowRefs.current.slice(0, rowsCount);
        const startY = e.clientY;
        // Шаг = высота строки + вертикальный gap ленты (меряем по соседям)
        const gap = (rows[1] && rows[0])
            ? Math.max(0, rows[1].offsetTop - rows[0].offsetTop - rows[0].offsetHeight)
            : 6;
        const step = el.offsetHeight + gap;
        let active = false;
        let target = fromPos;
        let raf = 0;
        let lastClientY = startY;

        const applyShifts = () => {
            raf = 0;
            const dy = lastClientY - startY;
            el.style.transform = `translateY(${dy}px) scale(1.015)`;
            const center = el.offsetTop + el.offsetHeight / 2 + dy;
            let t = 0;
            rows.forEach((node, i) => {
                if (!node || i === fromPos) return;
                if (node.offsetTop + node.offsetHeight / 2 < center) t++;
            });
            target = t;
            rows.forEach((node, i) => {
                if (!node || i === fromPos) return;
                let shift = 0;
                if (fromPos < target && i > fromPos && i <= target) shift = -step;
                else if (target < fromPos && i >= target && i < fromPos) shift = step;
                node.style.transform = shift ? `translateY(${shift}px)` : '';
            });
            const ghost = dropGhostRef.current;
            if (ghost) {
                let slotTop;
                if (target === fromPos) slotTop = el.offsetTop;
                else if (target > fromPos) {
                    const n = rows[target];
                    slotTop = n ? n.offsetTop + n.offsetHeight + gap - step : el.offsetTop;
                } else {
                    slotTop = rows[target] ? rows[target].offsetTop : el.offsetTop;
                }
                ghost.style.top = `${slotTop}px`;
                ghost.style.height = `${el.offsetHeight}px`;
            }
        };

        const onMove = (ev) => {
            lastClientY = ev.clientY;
            if (!active) {
                // Порог 6px отделяет перетаскивание от обычного клика
                if (Math.abs(ev.clientY - startY) < 6) return;
                active = true;
                suppressRowClickRef.current = true;
                // Entrance-анимация строк держит transform через fill-mode —
                // глушим её, иначе инлайновые transform не применятся
                rows.forEach((node) => { if (node) node.style.animation = 'none'; });
                el.classList.add(s.rowReordering);
                rows.forEach((node, i) => {
                    if (!node || i === fromPos) return;
                    node.style.transition = 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)';
                });
                if (dropGhostRef.current) dropGhostRef.current.style.display = 'block';
                document.body.style.cursor = 'grabbing';
            }
            // Автоскролл страницы у краёв вьюпорта
            if (ev.clientY < 90) window.scrollBy(0, -9);
            else if (ev.clientY > window.innerHeight - 90) window.scrollBy(0, 9);
            if (!raf) raf = requestAnimationFrame(applyShifts);
            ev.preventDefault();
        };

        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            if (raf) cancelAnimationFrame(raf);
            document.body.style.cursor = '';
            if (dropGhostRef.current) dropGhostRef.current.style.display = 'none';
            rows.forEach((node) => {
                if (!node) return;
                node.style.transform = '';
                node.style.transition = '';
                // animation: none не возвращаем — иначе entrance-анимация
                // проиграется заново по всем строкам
            });
            el.classList.remove(s.rowReordering);
            if (!active) return;
            setTimeout(() => { suppressRowClickRef.current = false; }, 0);
            if (target !== fromPos) {
                setRowOrder(prev => {
                    const base = (prev && prev.length === rowsCount)
                        ? [...prev]
                        : Array.from({ length: rowsCount }, (_, i) => i);
                    const [moved] = base.splice(fromPos, 1);
                    base.splice(target, 0, moved);
                    return base;
                });
            }
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // Инлайн-редактор текстового поля: аккуратное поле + кнопка «применить».
    // Галочка/Enter применяют, Esc отменяет. Blur не коммитит, чтобы клик мимо
    // не открывал карточку и не сохранял случайно недописанное значение.
    const InlineTextEdit = ({ inputClass, onChangeValue = (v) => v, baseDate, inputProps }) => (
        <span className={s.inlineEdit} ref={editBoxRef} onClick={(e) => e.stopPropagation()}>
            <input
                type="text"
                className={inputClass}
                value={editingValue}
                autoFocus
                onChange={(e) => setEditingValue(onChangeValue(e.target.value))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitEdit(baseDate); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                }}
                {...inputProps}
            />
            <button
                type="button"
                className={s.inlineApply}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => { e.stopPropagation(); commitEdit(baseDate); }}
                aria-label="Применить"
            >
                <Check size={13} strokeWidth={2.6} aria-hidden="true" />
            </button>
        </span>
    );

    const copyTxId = (txId) => {
        if (navigator.clipboard) navigator.clipboard.writeText(txId).catch(() => {});
        setTxIdCopied(true);
        setTimeout(() => setTxIdCopied(false), 1600);
    };

    const handlePnlDateChange = (value) => {
        setPnlDate?.(value);
        setPnlDateUserEdited?.(true);
    };

    // Форматирование чисел по настройке конфига
    const formatCurrency = (number, format = siteSettings?.numberFormat || 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0';
        const parts = parseFloat(number).toFixed(2).split('.');
        const [integerPart, decimalPart] = parts;
        if (format === 'dot') {
            return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimalPart}`;
        }
        return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart}`;
    };

    const formatClients = (number) => {
        const num = parseInt(number, 10);
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    // ── Статистика: перегенерация при смене даты P&L (логика оригинала)
    const generateStatsData = useMemo(() => {
        const diario = (Math.random() * (3891.47 - 1267.84) + 1267.84);
        const acumulado = (Math.random() * (991.47 - 716.62) + 716.62);
        const finalDailyValue = 8 + (parseFloat(diario) / 100);
        const finalAccumulatedValue = 3 + (parseFloat(acumulado) / 100);
        // Распределение дохода по активам: случайные веса, нормированные к 100%.
        // Крипта из админки всегда с наибольшей долей и жёстко закреплена первой
        const symbols = Object.keys(COIN_META);
        const primary = symbols.includes(selectedCrypto) ? selectedCrypto : 'BNB';
        const rawWeights = symbols.map(sym => (sym === primary ? 3 + Math.random() * 2 : 0.4 + Math.random() * 1.6));
        const weightSum = rawWeights.reduce((a, b) => a + b, 0);
        const shares = symbols.map((sym, i) => ({ symbol: sym, share: (rawWeights[i] / weightSum) * 100 }));
        const assetSplit = [
            shares.find(item => item.symbol === primary),
            ...shares.filter(item => item.symbol !== primary).sort((a, b) => b.share - a.share),
        ];

        return {
            chartData: generateRandomChartData(finalDailyValue, finalAccumulatedValue),
            averagePnL: ((diario + acumulado) / 2).toFixed(2),
            spreadsValue: (Math.random() * (0.08 - 0.01) + 0.01).toFixed(2),
            dailyVolume: formatCurrency((Math.random() * (1249290.84 - 448921.98)) + 448921.98),
            randomDiarioPnL: diario.toFixed(2),
            randomAcumuladoPnL: acumulado.toFixed(2),
            assetSplit,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pnlDate, siteSettings?.numberFormat, selectedCrypto]);

    const { chartData, averagePnL, spreadsValue, dailyVolume, randomDiarioPnL, randomAcumuladoPnL, assetSplit } = generateStatsData;

    // ── Имена клиентов из файла настроек
    useEffect(() => {
        const loadNames = async () => {
            try {
                const namesFileName = siteSettings.namesFile || 'names.txt';
                let fileOnly = (namesFileName || '').split('/').pop();
                if (fileOnly && !fileOnly.endsWith('.txt')) fileOnly = fileOnly + '.txt';
                if (!fileOnly || fileOnly === '.txt') fileOnly = 'names.txt';

                const candidateUrls = [
                    `/api/files/names/${encodeURIComponent(fileOnly)}`,
                    `/data/names/${fileOnly}`,
                    `/names/${fileOnly}`,
                    `/${namesFileName}`,
                ];

                let text = '';
                for (const url of candidateUrls) {
                    try {
                        const res = await fetch(url, { cache: 'no-store' });
                        if (!res.ok) continue;
                        const body = await res.text();
                        const ct = (res.headers.get('content-type') || '').toLowerCase();
                        const looksLikeHtml = /<html|<!doctype/i.test(body);
                        if ((ct.includes('text') || ct === '') && !looksLikeHtml) {
                            text = body;
                            break;
                        }
                    } catch (_) { /* пробуем следующий адрес */ }
                }
                if (!text) throw new Error('Names file not found');

                const namesArray = text
                    .split('\n')
                    .map(name => name.trim())
                    .filter(name => name.length > 0 && !/^<\/?[a-z]/i.test(name));
                if (namesArray.length === 0) throw new Error('Names file is empty');
                setAllNames(namesArray);
            } catch (_) {
                setAllNames([
                    'Alexander Johnson', 'Maria Garcia', 'John Smith', 'Emma Wilson',
                    'David Brown', 'Sofia Martinez', 'Michael Davis', 'Isabella Rodriguez',
                    'Carlos Lopez', 'Ana Gonzalez', 'Robert Miller', 'Lucia Fernandez',
                ]);
            }
        };
        loadNames();
    }, [siteSettings.namesFile]);

    // ── Генерация 5000 транзакций (логика оригинала, включая otzovik-режим)
    useEffect(() => {
        if (allNames.length === 0) return;

        const generateUniqueNames = (count) => {
            const names = [...allNames];
            const assignedNames = [];
            for (let i = 0; i < count; i++) {
                if (names.length === 0) names.push(...allNames);
                const randomIndex = Math.floor(Math.random() * names.length);
                assignedNames.push(names.splice(randomIndex, 1)[0]);
            }
            return assignedNames;
        };

        // Выбор банка для ленты — рандом, но веса строго по порядку банков
        // (как они идут в настройках): верхние чуть чаще, ниже чуть реже,
        // при этом у самых нижних всё равно ненулевой шанс (floor), чтобы они
        // не пропадали. Не связано с панелью «Объём по банкам»
        const enabledBankNames = siteSettings?.banks
            ? Object.keys(siteSettings.banks).filter(b => siteSettings.banks[b]?.enabled === true)
            : [];
        const ORDER_DECAY = 0.85; // мягкое убывание веса вниз по списку
        const ORDER_FLOOR = 0.18; // минимальный вес — нижние банки всё равно видны
        const bankWeights = enabledBankNames.map((_, i) => Math.pow(ORDER_DECAY, i) + ORDER_FLOOR);
        const bankWeightSum = bankWeights.reduce((a, b) => a + b, 0);
        const pickBank = () => {
            if (enabledBankNames.length === 0) return getRandomNetwork(siteSettings);
            let r = Math.random() * bankWeightSum;
            for (let i = 0; i < enabledBankNames.length; i++) {
                r -= bankWeights[i];
                if (r <= 0) return enabledBankNames[i];
            }
            return enabledBankNames[enabledBankNames.length - 1];
        };

        const isOtzovikMode = siteSettings?.otzovikMode || false;
        let currentTimestamp = new Date().getTime();
        let minTimestamp = null;
        if (isOtzovikMode) {
            const now = new Date();
            const endOfWorkingDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 22, 0, 0);
            const startOfWorkingDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 0, 0);
            currentTimestamp = endOfWorkingDay.getTime();
            minTimestamp = startOfWorkingDay.getTime();
        }

        const data = Array.from({ length: totalElems }).map(() => {
            const minTimeDiff = 7 * 60 * 1000;
            const maxTimeDiff = 25 * 60 * 1000;
            currentTimestamp -= Math.floor(Math.random() * (maxTimeDiff - minTimeDiff + 1)) + minTimeDiff;

            if (isOtzovikMode && minTimestamp !== null && currentTimestamp < minTimestamp) {
                const previousDay = new Date(minTimestamp);
                previousDay.setDate(previousDay.getDate() - 1);
                previousDay.setHours(22, 0, 0, 0);
                const newStart = new Date(previousDay);
                newStart.setHours(10, 0, 0, 0);
                minTimestamp = newStart.getTime();
                currentTimestamp = previousDay.getTime();
            }

            return {
                date: new Date(currentTimestamp),
                amount: getRandomAmount((amount) => formatCurrency(amount), siteSettings),
                usdt: getRandomUSDT(),
                network: pickBank(),
                digits: getRandomDigits(),
                txId: generateTxId(),
            };
        });

        setRandomData(data);
        setNamesAssigned(generateUniqueNames(totalElems));
        setStatuses(Array(totalElems).fill('Success'));
        // Данные перегенерированы — сбрасываем одноразовые правки строк, чтобы
        // старые переопределения не «прилипли» к новым (другим) строкам по индексу
        setEditedRows({});
        setEditing(null);
        // Порядок строк и правки времени тоже одноразовые — сбрасываем
        setRowOrder([]);
        setTimeOverrides({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allNames, siteSettings?.banks, siteSettings?.numberFormat, siteSettings?.brokerAmountGeneration, siteSettings?.timezone, siteSettings?.otzovikMode, pnlDate]);

    // Фильтрация по рабочему дню 10:00–22:00 (как в оригинале)
    const currentData = randomData
        .filter(data => {
            const hour = new Date(data.date).getHours();
            return !(hour >= 22 || hour < 10);
        })
        .slice((currentPage - 1) * linesOnPage, currentPage * linesOnPage);

    // Общий объём фида для футера-сводки
    const feedTotalVolume = useMemo(() => {
        const total = randomData.reduce((acc, item) => {
            const cleaned = siteSettings?.numberFormat === 'dot'
                ? item.amount.replace(/\./g, '').replace(',', '.')
                : item.amount.replace(/,/g, '');
            return acc + (parseFloat(cleaned) || 0);
        }, 0);
        return formatCurrency(total);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [randomData, siteSettings?.numberFormat]);

    // ── Сумма отправленного за 24 часа
    const calculateSentToday = () => {
        const lastTransactions = randomData.slice(-39);
        const total = lastTransactions.reduce((acc, item) => {
            let cleaned;
            if (siteSettings?.numberFormat === 'dot') {
                cleaned = item.amount.replace(/\./g, '').replace(',', '.');
            } else {
                cleaned = item.amount.replace(/,/g, '');
            }
            return acc + parseFloat(cleaned);
        }, 0);
        return formatCurrency(total);
    };

    // ── Доли банков: честно считаются из сгенерированных транзакций ленты
    // Панель «Объём по банкам» — независимая от ленты генерация (как раньше):
    // явный лидер (~29–39%) и геометрическое убывание с джиттером. Пересобирается
    // при смене набора банков или даты P&L, стабильна между рендерами
    const bankSplit = useMemo(() => {
        const names = siteSettings?.banks
            ? Object.keys(siteSettings.banks).filter(b => siteSettings.banks[b]?.enabled === true)
            : [];
        if (!names.length) return [];
        const shuffled = [...names].sort(() => Math.random() - 0.5);
        const topShare = 0.29 + Math.random() * 0.10;
        const decay = 0.60 + Math.random() * 0.14;
        const tail = shuffled.slice(1).map((_, i) => Math.pow(decay, i + 1) * (0.85 + Math.random() * 0.3));
        const tailSum = tail.reduce((a, b) => a + b, 0) || 1;
        return shuffled
            .map((bank, i) => ({
                bank,
                share: (i === 0 ? topShare : tail[i - 1] * (1 - topShare) / tailSum) * 100,
            }))
            .sort((a, b) => b.share - a.share)
            .slice(0, 5);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteSettings?.banks, pnlDate]);

    const translateStatus = (status) => {
        switch (status.toLowerCase()) {
            case 'success': return t('brokerProgram.statuses.success', 'Успешно');
            case 'pending': return t('brokerProgram.statuses.pending', 'Ожидание');
            case 'in process': return t('brokerProgram.statuses.inProcess', 'В процессе');
            default: return status;
        }
    };

    const statusBadgeClass = (status) => {
        switch (status.toLowerCase()) {
            case 'success': return s.bGood;
            case 'in process': return s.bAmber;
            default: return s.bBlue;
        }
    };

    // Смена статуса по клику: Success → Pending → In Process → Success
    const changeStatus = (index) => {
        setStatuses(prev => {
            const next = [...prev];
            switch (next[index]) {
                case 'Success': next[index] = 'Pending'; break;
                case 'Pending': next[index] = 'In Process'; break;
                case 'In Process': next[index] = 'Success'; break;
                default: break;
            }
            return next;
        });
    };

    // ── Форматирование дат по настройкам dateTimeSettings
    const formatTxDate = (dateInput, { withTime = true } = {}) => {
        const dateTimeSettings = siteSettings?.dateTimeSettings || {
            showDateTime: true, dateFormat: 'dd/MM/yyyy', timeFormat: '24h',
        };
        const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
        if (withTime && dateTimeSettings.showDateTime === true) {
            options.hour = '2-digit';
            options.minute = '2-digit';
            options.hour12 = dateTimeSettings.timeFormat === '12h';
        }
        const dateFormat = dateTimeSettings.dateFormat || 'dd/MM/yyyy';
        const locale = dateFormat.includes('MM/dd') ? 'en-US' : dateFormat.includes('yyyy-MM') ? 'en-CA' : 'es-AR';
        let formatted = new Intl.DateTimeFormat(locale, options).format(new Date(dateInput));
        if (dateFormat === 'dd.MM.yyyy') formatted = formatted.replace(/\//g, '.');
        else if (dateFormat === 'dd-MM-yyyy') formatted = formatted.replace(/\//g, '-');
        return formatted;
    };

    // Дата в Spreads: парсим dd/MM/yy и форматируем по настройкам
    const formatSpreadsDate = () => {
        if (!pnlDate) return '';
        try {
            const parts = pnlDate.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                const fullYear = year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
                return formatTxDate(new Date(fullYear, month, day), { withTime: false });
            }
        } catch (_) { /* вернём как есть */ }
        return pnlDate;
    };

    // Дата в модалке: в otzovik-режиме дата из Spreads + время транзакции
    const formatModalDate = (rowData) => {
        if (siteSettings?.otzovikMode && pnlDate) {
            try {
                const parts = pnlDate.split('/');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    const fullYear = year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
                    const txDate = new Date(rowData.date);
                    return formatTxDate(new Date(
                        fullYear, month, day,
                        txDate.getHours(), txDate.getMinutes(), txDate.getSeconds()
                    ));
                }
            } catch (_) { /* fallback ниже */ }
            return pnlDate;
        }
        return formatTxDate(rowData.date);
    };

    const handleRowClick = (data, name, status) => {
        setSelectedRowData({
            name,
            amount: data.amount,
            network: data.network,
            cbucvu: data.fullAccount ? data.fullAccount : `${'*'.repeat(4)}${data.digits}`,
            date: data.date,
            status,
            txId: data.txId || generateTxId(),
        });
        setIsAccountVisible(false); // при каждом открытии счёт скрыт (только ****последние4)
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalClosing(true);
        setTimeout(() => {
            setIsModalOpen(false);
            setIsModalClosing(false);
        }, 200);
    };

    // Count-up: сумма «догоняет» значение при открытии модалки (~650мс, ease-out)
    useEffect(() => {
        if (!isModalOpen || !selectedRowData) return undefined;
        // Сумма может прийти и числом (maxTotalMoney юзера) — приводим к строке
        const str = String(selectedRowData.amount ?? '');
        const raw = parseFloat(
            siteSettings?.numberFormat === 'dot'
                ? str.replace(/\./g, '').replace(',', '.')
                : str.replace(/,/g, '')
        ) || 0;
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (reduced) {
            setCountedAmount(str);
            return undefined;
        }
        const DURATION = 650;
        const t0 = performance.now();
        let raf = 0;
        const tick = (now) => {
            const p = Math.min(1, (now - t0) / DURATION);
            const eased = 1 - Math.pow(1 - p, 3);
            setCountedAmount(p >= 1 ? str : formatCurrency(raw * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, selectedRowData]);

    // Таймлайн статуса вывода: сколько шагов пройдено
    // Финальный шаг — «Отправлено»: деньги ушли клиенту
    const timelineSteps = [
        t('brokerProgram.timeline.credited', 'Зачислено'),
        t('brokerProgram.timeline.processing', 'В обработке'),
        t('brokerProgram.timeline.sent', 'Отправлено'),
    ];
    const timelineReached = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'success': return 3;
            case 'in process': return 2;
            default: return 1;
        }
    };

    // Сумма в модалке: копейки мельче и приподняты (стиль дэшборда)
    const splitAmount = (amount) => {
        const str = String(amount || '');
        let parts = null;
        if (str.includes(',') && str.lastIndexOf(',') > str.lastIndexOf('.')) {
            parts = [str.slice(0, str.lastIndexOf(',')), str.slice(str.lastIndexOf(',') + 1)];
        } else if (str.includes('.')) {
            parts = [str.slice(0, str.lastIndexOf('.')), str.slice(str.lastIndexOf('.') + 1)];
        }
        return parts && parts[1]?.length === 2 ? parts : [str, null];
    };

    // Кастомный тултип графика P&L в токенах Nova
    const PnLTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const dailyPnL = payload.find(p => p.dataKey === 'greenValue')?.value || 0;
        const accumulatedPnL = payload.find(p => p.dataKey === 'yellowValue')?.value || 0;
        const baseDaily = 8;
        const baseAccumulated = 3;
        const currentDailyProgress = ((dailyPnL - baseDaily) / baseDaily * 100);
        const currentAccumulatedProgress = ((accumulatedPnL - baseAccumulated) / baseAccumulated * 100);
        return (
            <div className={s.chartTip}>
                <div className={s.chartTipTitle}>
                    <span className={s.tipDotGood} aria-hidden="true" />
                    {t('brokerProgram.pnlSummary.title', 'Отчет сводки P&L')}
                </div>
                <div className={s.chartTipRow}>
                    <span>{t('brokerProgram.pnlSummary.dailyPnL', 'P&L Дневной (%)')}</span>
                    <b className={currentDailyProgress >= 0 ? s.tipGood : s.tipBad}>
                        {currentDailyProgress >= 0 ? '+' : ''}{currentDailyProgress.toFixed(1)}%
                    </b>
                </div>
                <div className={s.chartTipRow}>
                    <span>{t('brokerProgram.pnlSummary.cumulativePnL', 'P&L Накопительный (%)')}</span>
                    <b className={currentAccumulatedProgress >= 0 ? s.tipGood : s.tipBad}>
                        {currentAccumulatedProgress >= 0 ? '+' : ''}{currentAccumulatedProgress.toFixed(1)}%
                    </b>
                </div>
            </div>
        );
    };

    const enabledBanks = siteSettings?.banks
        ? Object.keys(siteSettings.banks).filter(b => siteSettings.banks[b]?.enabled === true)
        : [];

    const brokerName = siteSettings.defaultUserName || 'Lobruno Kevin Antonio';
    const brokerLicense = siteSettings.brokerLicenseNumber || '918552';

    return (
        <div className={s.page}>
            {/* ── Панель действий */}
            <div className={s.actionsRow}>
                <button type="button" className={`${s.actionBtn} ${s.actionPrimary}`}>
                    <UserPlus size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.addClient', 'Добавить клиента')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <Building2 size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.brokerCenter', 'Центр брокера')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <ClipboardList size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.orderCenter', 'Центр заказов')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <SlidersHorizontal size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.options', 'Опции')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <FileBarChart2 size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.reports', 'Отчёты P&L')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <PieChart size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.assets', 'Распределение активов')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <Layers size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.defi', 'DeFi Стейкинг')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <KeyRound size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.apiKeys', 'API-ключи')}
                </button>
                <button type="button" className={s.actionBtn}>
                    <ShieldCheck size={15} strokeWidth={2.1} aria-hidden="true" />
                    {t('brokerProgram.headerButtons.kyc', 'Верификация KYC')}
                </button>
            </div>

            {/* ── Обзор брокера: все метрики + график + факты в одной карточке */}
            <div className={s.top}>
                <section className={s.overviewWrap} aria-label={t('brokerProgram.statistics.brokerStatistics', 'Статистика брокера')}>
                    <div className={s.tabsRow}>
                        <span className={s.tab}>
                            <BadgeCheck size={12} strokeWidth={2} aria-hidden="true" />
                            {t('brokerProgram.statistics.brokerStatistics', 'Статистика брокера')}
                        </span>
                        <span className={`${s.tab} ${s.tabRight}`}>
                            {t('brokerProgram.pnlSummary.title', 'Отчет сводки P&L')}
                            <LineChartIcon size={12} strokeWidth={2} aria-hidden="true" />
                        </span>
                    </div>
                    <div className={s.pnlCard}>
                        {/* Полоса метрик: P&L с цветными рельсами + статистика брокера */}
                        <div className={s.headStrip}>
                            <div className={s.hStat}>
                                <span className={`${s.hRail} ${s.railGood}`} aria-hidden="true" />
                                <div className={s.hBody}>
                                    <span className={s.hLabel}>{t('brokerProgram.pnlSummary.dailyPnL', 'P&L Дневной (%)')}</span>
                                    <span className={`${s.hValue} ${s.kpiGood}`}>+{randomDiarioPnL}%</span>
                                </div>
                            </div>
                            <div className={s.hStat}>
                                <span className={`${s.hRail} ${s.railAmber}`} aria-hidden="true" />
                                <div className={s.hBody}>
                                    <span className={s.hLabel}>{t('brokerProgram.pnlSummary.cumulativePnL', 'P&L Накопительный (%)')}</span>
                                    <span className={`${s.hValue} ${s.kpiAmber}`}>+{randomAcumuladoPnL}%</span>
                                </div>
                            </div>
                            <div className={s.hStat} onDoubleClick={() => setIsEditing(true)}>
                                <div className={s.hBody}>
                                    <span className={s.hLabel}>
                                        <Users size={11} strokeWidth={2.1} aria-hidden="true" />
                                        {t('brokerProgram.statistics.totalClients', 'Общее количество клиентов')}
                                    </span>
                                    <span className={s.hValue}>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={clientsCount}
                                                onBlur={() => setIsEditing(false)}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    if (!isNaN(value)) setClientsCount(value);
                                                }}
                                                className={s.editInput}
                                                autoFocus
                                            />
                                        ) : formatClients(clientsCount)}
                                    </span>
                                </div>
                            </div>
                            <div className={s.hStat}>
                                <div className={s.hBody}>
                                    <span className={s.hLabel}>
                                        <Percent size={11} strokeWidth={2.1} aria-hidden="true" />
                                        {t('brokerProgram.statistics.averagePnL', 'Средний P&L (%)')}
                                    </span>
                                    <span className={`${s.hValue} ${s.kpiGood}`}>
                                        +{averagePnL}%
                                        <TrendingUp size={13} strokeWidth={2.2} aria-hidden="true" />
                                    </span>
                                </div>
                            </div>
                            <div className={s.hStat}>
                                <div className={s.hBody}>
                                    <span className={s.hLabel}>
                                        <Send size={11} strokeWidth={2.1} aria-hidden="true" />
                                        {t('brokerProgram.statistics.sent24h', 'Отправлено (24ч)')}
                                    </span>
                                    <span className={s.hValue}>
                                        <span className={s.hUnit}>{currencyFormat}</span>
                                        {calculateSentToday()}
                                    </span>
                                </div>
                            </div>
                            <div className={s.hStat}>
                                <div className={s.hBody}>
                                    <span className={s.hLabel}>
                                        <Landmark size={11} strokeWidth={2.1} aria-hidden="true" />
                                        {t('brokerProgram.statistics.mainCurrency', 'Основная валюта')}
                                    </span>
                                    <span className={s.hValue}>
                                        <FlagIcon siteSettings={siteSettings} className={s.hFlag} alt="" />
                                        {currencyFormat}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Средняя зона: график + доход по активам + объём по банкам */}
                        <div className={s.midZone}>
                            <div className={s.chartArea}>
                                {/* Абсолютная обёртка даёт ResponsiveContainer определённую
                                    высоту: 100% от flex-ячейки с min-height он измерить не может */}
                                <div className={s.chartInner}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={chartData} margin={{ top: 6, right: 2, left: 2, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="brokerGood" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="var(--good)" stopOpacity={0.22} />
                                                    <stop offset="100%" stopColor="var(--good)" stopOpacity={0} />
                                                </linearGradient>
                                                <linearGradient id="brokerAmber" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#E8B93E" stopOpacity={0.18} />
                                                    <stop offset="100%" stopColor="#E8B93E" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="var(--hairline)" vertical={false} />
                                            <Tooltip content={<PnLTooltip />} cursor={{ stroke: 'var(--hairline-strong)' }} />
                                            <Area type="monotone" dataKey="greenValue" stroke="var(--good)" strokeWidth={2} fill="url(#brokerGood)" dot={false} activeDot={{ r: 3.5, strokeWidth: 0 }} strokeLinecap="round" />
                                            <Area type="monotone" dataKey="yellowValue" stroke="#E8B93E" strokeWidth={2} fill="url(#brokerAmber)" dot={false} activeDot={{ r: 3.5, strokeWidth: 0 }} strokeLinecap="round" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Доход по активам: доли нормированы к 100%, выбранная монета первая */}
                            <div className={s.midPanel}>
                                <span className={s.midTitle}>{t('brokerProgram.overview.byAsset', 'Доход по активам')}</span>
                                <div className={s.splitList}>
                                    {assetSplit.map(({ symbol, share }) => (
                                        <div key={symbol} className={s.splitRow}>
                                            <span className={s.splitName}>
                                                <img src={COIN_META[symbol].icon} alt="" className={s.splitIcon} />
                                                {symbol}
                                            </span>
                                            <span className={s.splitTrack} aria-hidden="true">
                                                <i style={{ width: `${share}%`, background: COIN_META[symbol].color }} />
                                            </span>
                                            <span className={s.splitPct}>{share.toFixed(1)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Объём по банкам: доли из реальных сумм сгенерированной ленты */}
                            <div className={s.midPanel}>
                                <span className={s.midTitle}>{t('brokerProgram.overview.byBank', 'Объём по банкам')}</span>
                                <div className={s.splitList}>
                                    {bankSplit.map(({ bank, share }) => {
                                        const iconSrc = getBankIcon(bank, siteSettings);
                                        return (
                                            <div key={bank} className={s.splitRow}>
                                                <span className={s.splitName} title={bank}>
                                                    {iconSrc
                                                        ? <img src={iconSrc} alt="" className={s.splitIcon} />
                                                        : <Landmark size={12} strokeWidth={2} className={s.splitFallback} aria-hidden="true" />}
                                                    {bank}
                                                </span>
                                                <span className={s.splitTrack} aria-hidden="true">
                                                    <i style={{ width: `${share}%` }} className={s.splitAccent} />
                                                </span>
                                                <span className={s.splitPct}>{share.toFixed(1)}%</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Нижняя полоса: спреды / активы / объём */}
                        <div className={s.pnlFoot}>
                            <div className={s.footStat}>
                                <span className={s.footLabel}>{t('brokerProgram.pnlSummary.spreads', 'Спреды')}</span>
                                <span className={s.footValue}>
                                    {spreadsValue}
                                    <span
                                        className={s.footDate}
                                        onDoubleClick={siteSettings?.otzovikMode ? () => setIsEditingDate(true) : undefined}
                                        style={{ cursor: siteSettings?.otzovikMode ? 'pointer' : 'default' }}
                                    >
                                        {isEditingDate && siteSettings?.otzovikMode ? (
                                            <input
                                                type="text"
                                                value={pnlDate}
                                                onBlur={() => setIsEditingDate(false)}
                                                onChange={(e) => handlePnlDateChange(e.target.value)}
                                                className={s.editInput}
                                                autoFocus
                                            />
                                        ) : (
                                            <>
                                                <CalendarDays size={10} strokeWidth={2} aria-hidden="true" />
                                                {formatSpreadsDate()}
                                            </>
                                        )}
                                    </span>
                                </span>
                            </div>
                            <div className={s.footStat}>
                                <span className={s.footLabel}>{t('brokerProgram.statistics.tradableAssets', 'Торгуемые активы')}</span>
                                <span className={s.footValue}>{selectedCrypto || 'ETH'}</span>
                            </div>
                            <div className={s.footStat}>
                                <span className={s.footLabel}>{t('brokerProgram.statistics.dailyVolume', 'Дневной объем')} ({currencyFormat})</span>
                                <span className={s.footValue}>{currencyFormat} {dailyVolume}</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* ── Последние выводы */}
            <section className={s.feedWrap} aria-label={t('brokerProgram.table.brokerTitle', 'Последние выводы / Брокер:')}>
                <span className={s.tab}>
                    <Landmark size={12} strokeWidth={2} aria-hidden="true" />
                    {t('brokerProgram.table.brokerTitle', 'Последние выводы / Брокер:')} {brokerName}
                    <span className={s.licenseChip}>#{brokerLicense}</span>
                    <span className={s.liveBadge}>
                        <i className={s.liveDot} aria-hidden="true" />
                        {t('brokerProgram.table.live', 'LIVE')}
                    </span>
                </span>
                <div className={s.feedCard}>
                    {enabledBanks.length === 0 ? (
                        <div className={s.noBanks}>
                            ⚠️ Нет включенных банков!<br />
                            Включите банки в настройках для отображения данных.
                        </div>
                    ) : (
                        <>
                        {/* Заголовки колонок в биржевом стиле */}
                        <div className={s.feedHead} aria-hidden="true">
                            <span>{t('brokerProgram.table.client', 'Клиент')}</span>
                            <span className={s.feedHeadBank}>{t('brokerProgram.table.bank', 'Банк-получатель')}</span>
                            <span className={s.feedHeadFlow} />
                            <span className={s.feedHeadRight}>
                                {t('brokerProgram.table.amount', 'Сумма')} / {t('brokerProgram.table.status', 'Статус')}
                            </span>
                        </div>
                        <div className={s.feed}>
                            {(() => {
                                // Скользящее окно семейств цветов: похожие аватары не идут подряд
                                const recentAvaFamilies = [];
                                // Branded: фиксируем самую свежую (верхнюю) строку под текущего
                                // клиента — имя и аватар из настроек, банк и последние 4 цифры
                                // счёта из админки (панель вывода).
                                const brandName = (siteSettings.defaultUserName || '').trim();
                                // Бренд-старт: банк всегда Lemon Cash (берём точное имя из конфига,
                                // чтобы подхватилась его иконка), сумма и последние 4 цифры — фиксы.
                                const brandBank = (() => {
                                    const banks = siteSettings?.banks ? Object.keys(siteSettings.banks) : [];
                                    return banks.find(b => /lemon/i.test(b)) || 'Lemon Cash';
                                })();
                                const brandDigits = '7769';
                                const brandAmount = formatCurrency(4721831.29);
                                const brandAvatar = resolveAvatarSrc(siteSettings.defaultUserAvatar);
                                // Текущее время в таймзоне страны из конфига (не по правилам генерации)
                                const brandTime = (() => {
                                    const use12 = siteSettings?.dateTimeSettings?.timeFormat === '12h';
                                    const opts = { hour: '2-digit', minute: '2-digit', hour12: use12 };
                                    if (siteSettings?.timezone) opts.timeZone = siteSettings.timezone;
                                    try {
                                        return new Intl.DateTimeFormat('es-AR', opts).format(new Date());
                                    } catch (_) {
                                        return new Intl.DateTimeFormat('es-AR', { hour: '2-digit', minute: '2-digit', hour12: use12 }).format(new Date());
                                    }
                                })();
                                // «Показать в бинпале» (серверный режим, отдельно от лаунчера):
                                // верхняя строка = данные текущего юзера из админки/настроек.
                                const userName = (nameUser || confirmedNameUser || '').trim();
                                const userBank = network;
                                const userDigits = String(cryptoAdress || '').replace(/\D/g, '').slice(-4);
                                // Сумму юзера приводим к общему формату (точки/запятые из конфига)
                                const userAmountNum = parseAmountLoose(maxTotalMoney);
                                const userAmount = isNaN(userAmountNum) ? String(maxTotalMoney ?? '') : formatCurrency(userAmountNum);
                                // Аватар из настроек — это «личность» дефолтного имени. Показываем
                                // его только если имя юзера совпадает с дефолтным, иначе — инициалы
                                const isDefaultIdentity = userName &&
                                    userName.toLowerCase() === (siteSettings.defaultUserName || '').trim().toLowerCase();
                                const userAvatar = isDefaultIdentity ? resolveAvatarSrc(siteSettings.defaultUserAvatar) : null;
                                const userFullAccount = String(cryptoAdress || '').trim();
                                return currentData.map((slotData, index) => {
                                // index — ПОЗИЦИЯ строки в списке (за ней закреплено время),
                                // contentIdx — контент, который сейчас стоит на этой позиции
                                // (перетаскивание двигает контент, но не время)
                                const contentIdx = rowOrder[index] != null ? rowOrder[index] : index;
                                const data = currentData[contentIdx] || slotData;
                                const globalIndex = contentIdx;
                                const isBrandTop = BRANDED && contentIdx === 0;
                                // Юзер-строка активна только на сервере (не в лаунчере);
                                // привязана к контенту — двигается вместе со строкой
                                const isUserTop = !BRANDED && showInBinpanel && contentIdx === 0;
                                const isTop = isBrandTop || isUserTop;
                                // Значения верхней строки: бренд (хардкод лаунчера) или юзер (из админки)
                                const topName = isBrandTop ? brandName : (isUserTop ? userName : '');
                                const topBank = isBrandTop ? brandBank : (isUserTop ? userBank : '');
                                const topDigits = isBrandTop ? brandDigits : (isUserTop ? userDigits : '');
                                const topAmount = isBrandTop ? brandAmount : userAmount;
                                const topAvatar = isBrandTop ? brandAvatar : userAvatar;
                                const topBankIcon = isBrandTop ? (imgCoin?.img || null) : (imgCoin?.img || null);
                                const topFullAccount = isBrandTop ? '0000168300000028897769' : userFullAccount;
                                const ov = editedRows[globalIndex] || {};
                                const baseName = (isTop && topName) ? topName : (namesAssigned[globalIndex] || '...');
                                const name = ov.name != null ? ov.name : baseName;
                                const status = statuses[globalIndex] || 'Success';
                                // Время — от ПОЗИЦИИ (slotData), а не от контента
                                const effDate = timeOverrides[index] || slotData.date;
                                const bankName = ov.network != null ? ov.network : ((isTop && topBank) ? topBank : data.network);
                                const digits = ov.digits != null ? ov.digits : ((isTop && topDigits) ? topDigits : data.digits);
                                const amountText = ov.amount != null ? ov.amount : (isTop ? topAmount : data.amount);
                                const txId = ov.txId != null ? ov.txId : data.txId;
                                const formattedDate = formatTxDate(effDate);
                                const timeOnly = (isTop && index === 0 && !timeOverrides[index]) ? brandTime : (formattedDate.split(' ')[1] || formattedDate);
                                const bankIconSrc = getBankIcon(bankName, siteSettings) || (isTop ? topBankIcon : null);
                                const avatarImg = isTop ? topAvatar : null;
                                const rowData = {
                                    ...data,
                                    date: effDate,
                                    network: bankName,
                                    digits,
                                    amount: amountText,
                                    txId,
                                    fullAccount: (isTop && ov.digits == null) ? topFullAccount : undefined,
                                };
                                const avaGradient = pickClientGradient(name, recentAvaFamilies);
                                // Правка идёт в этой строке? (время — по позиции, остальное — по контенту)
                                const isRowEditing = editing && (editing.field === 'time' ? editing.index === index : editing.index === globalIndex);

                                return (
                                    <div
                                        key={data.txId || `c${contentIdx}`}
                                        ref={(node) => { rowRefs.current[index] = node; }}
                                        className={`${s.txRow} ${isRowEditing ? s.txRowRaised : ''}`}
                                        style={{ animationDelay: `${Math.min(index * 35, 280)}ms` }}
                                        onClick={() => {
                                            if (suppressRowClickRef.current) return;
                                            // Выделение начали в поле, а отпустили за краем — не закрываем
                                            if (pressStartedInEditorRef.current) { pressStartedInEditorRef.current = false; return; }
                                            if (editingRef.current) { cancelEdit(); return; }
                                            handleRowClick(rowData, name, status);
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {/* Невидимая зона перетаскивания слева от аватарки */}
                                        <span
                                            className={s.dragZone}
                                            onPointerDown={(e) => handleRowDragStart(e, index, currentData.length)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-hidden="true"
                                        />
                                        <div className={s.txLeft}>
                                            {avatarImg
                                                ? <BrandAvatar src={avatarImg} initials={clientInitials(name)} gradient={avaGradient} />
                                                : <span className={s.clientAva} style={{ background: avaGradient }} aria-hidden="true">{clientInitials(name)}</span>}
                                            <div className={s.txMain}>
                                                {isEditingCell(globalIndex, 'name') ? (
                                                    InlineTextEdit({ inputClass: s.txNameEdit, baseDate: effDate })
                                                ) : (
                                                    <span
                                                        className={s.txName}
                                                        onClick={canEditField('name') ? (e) => e.stopPropagation() : undefined}
                                                        onDoubleClick={canEditField('name') ? (e) => startEdit(globalIndex, 'name', name, e) : undefined}
                                                    >
                                                        {name}
                                                    </span>
                                                )}
                                                <span className={s.txMeta}>
                                                    {isEditingCell(globalIndex, 'txId') ? (
                                                        InlineTextEdit({ inputClass: s.txMetaEdit, baseDate: effDate })
                                                    ) : (
                                                        <code
                                                            className={s.txId}
                                                            onClick={canEditField('txId') ? (e) => e.stopPropagation() : undefined}
                                                            onDoubleClick={canEditField('txId') ? (e) => startEdit(globalIndex, 'txId', txId, e) : undefined}
                                                        >#{txId}</code>
                                                    )}
                                                    <i className={s.txDot} aria-hidden="true" />
                                                    <Clock size={10} strokeWidth={2} aria-hidden="true" />
                                                    {isEditingCell(index, 'time') ? (
                                                        InlineTextEdit({ inputClass: s.txMetaEdit, baseDate: effDate })
                                                    ) : (
                                                        <span
                                                            onClick={canEditField('time') ? (e) => e.stopPropagation() : undefined}
                                                            onDoubleClick={canEditField('time') ? (e) => startEdit(index, 'time', toEditableTime(effDate), e) : undefined}
                                                        >{timeOnly}</span>
                                                    )}
                                                </span>
                                            </div>
                                        </div>

                                        <div className={s.txBank}>
                                            {bankIconSrc ? (
                                                <span className={s.bankPlate}>
                                                    <BankLogo src={bankIconSrc} alt={bankName} />
                                                </span>
                                            ) : (
                                                <span className={`${s.bankPlate} ${s.bankFallback}`}>
                                                    <Landmark size={13} strokeWidth={2} aria-hidden="true" />
                                                </span>
                                            )}
                                            <div className={s.bankInfo}>
                                                {isEditingCell(globalIndex, 'network') ? (
                                                    <span className={s.bankSelectWrap} onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            type="button"
                                                            className={s.bankSelectBtn}
                                                            onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                                                            aria-expanded="true"
                                                        >
                                                            <span className={s.bankSelectValue}>{bankName}</span>
                                                            <ChevronDown size={13} strokeWidth={2} className={`${s.bankSelectChevron} ${s.bankSelectChevronOpen}`} aria-hidden="true" />
                                                        </button>
                                                        <div className={s.bankDropdown}>
                                                            {enabledBanks.map((b) => {
                                                                const optIcon = getBankIcon(b, siteSettings);
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        key={b}
                                                                        className={`${s.bankOption} ${b === bankName ? s.bankOptionSelected : ''}`}
                                                                        onClick={(e) => { e.stopPropagation(); commitBank(globalIndex, b); }}
                                                                    >
                                                                        {optIcon
                                                                            ? <span className={s.bankOptionPlate}><BankLogo src={optIcon} alt="" /></span>
                                                                            : <span className={`${s.bankOptionPlate} ${s.bankOptionPlateEmpty}`}><Landmark size={12} strokeWidth={2} aria-hidden="true" /></span>}
                                                                        <span className={s.bankOptionName}>{b}</span>
                                                                        {b === bankName && <Check size={14} strokeWidth={2.4} className={s.bankOptionCheck} aria-hidden="true" />}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </span>
                                                ) : (
                                                    <span
                                                        className={s.bankName}
                                                        onClick={canEditField('network') ? (e) => e.stopPropagation() : undefined}
                                                        onDoubleClick={canEditField('network') ? (e) => startEdit(globalIndex, 'network', bankName, e) : undefined}
                                                    >{bankName}</span>
                                                )}
                                                <span className={s.bankAccount}>
                                                    <CreditCard size={10} strokeWidth={2} aria-hidden="true" />
                                                    ••••{isEditingCell(globalIndex, 'digits') ? (
                                                        InlineTextEdit({
                                                            inputClass: s.digitsEdit,
                                                            baseDate: effDate,
                                                            onChangeValue: (v) => v.replace(/\D/g, '').slice(0, 4),
                                                            inputProps: { maxLength: 4, inputMode: 'numeric' },
                                                        })
                                                    ) : (
                                                        <span
                                                            onClick={canEditField('digits') ? (e) => e.stopPropagation() : undefined}
                                                            onDoubleClick={canEditField('digits') ? (e) => startEdit(globalIndex, 'digits', digits, e) : undefined}
                                                        >{digits}</span>
                                                    )}
                                                    <FlagIcon siteSettings={siteSettings} className={s.txFlag} alt="" />
                                                </span>
                                            </div>
                                        </div>

                                        {/* Линия потока средств банк → сумма: живёт по статусу транзакции */}
                                        <div
                                            className={`${s.txFlow} ${status === 'Success' ? s.flowGood : status === 'In Process' ? s.flowMoving : s.flowWait}`}
                                            aria-hidden="true"
                                        >
                                            <i className={s.flowLine}>
                                                {status === 'In Process' && <i className={s.flowDot} />}
                                            </i>
                                            <span className={s.flowTip}>
                                                {status === 'Success' && <Check size={12} strokeWidth={3} />}
                                                {status === 'In Process' && <ArrowRight size={12} strokeWidth={2.8} />}
                                                {status !== 'Success' && status !== 'In Process' && <Clock size={12} strokeWidth={2.6} />}
                                            </span>
                                        </div>
                                        <div className={s.txRight}>
                                            {isEditingCell(globalIndex, 'amount') ? (
                                                <span className={s.txAmount}>
                                                    <span className={s.txCurrency}>{currencyFormat}</span>
                                                    {InlineTextEdit({ inputClass: s.amountEdit, baseDate: effDate })}
                                                </span>
                                            ) : (
                                                <span
                                                    className={s.txAmount}
                                                    onClick={canEditField('amount') ? (e) => e.stopPropagation() : undefined}
                                                    onDoubleClick={canEditField('amount') ? (e) => startEdit(globalIndex, 'amount', amountText, e) : undefined}
                                                >
                                                    <span className={s.txCurrency}>{currencyFormat}</span>
                                                    {(() => {
                                                        const [intPart, cents] = splitAmount(amountText);
                                                        return (
                                                            <span className={s.txAmountNum}>
                                                                {intPart}
                                                                {cents !== null && <span className={s.txCents}>{cents}</span>}
                                                            </span>
                                                        );
                                                    })()}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className={`${s.txStatus} ${statusBadgeClass(status)}`}
                                                onClick={(e) => { e.stopPropagation(); changeStatus(globalIndex); }}
                                            >
                                                {translateStatus(status)}
                                            </button>
                                        </div>
                                    </div>
                                );
                                });
                            })()}
                            {/* Пунктирный «призрак» слота при перетаскивании строки */}
                            <div className={s.dropGhost} ref={dropGhostRef} aria-hidden="true" />
                        </div>

                        {/* Футер-сводка: количество и общий объём фида */}
                        <div className={s.feedFoot}>
                            <span className={s.feedFootItem}>
                                <ClipboardList size={12} strokeWidth={2} aria-hidden="true" />
                                {t('brokerProgram.table.transactions', 'Транзакции')}:
                                <b>{randomData.length}</b>
                            </span>
                            <span className={s.feedFootItem}>
                                {t('brokerProgram.table.totalVolume', 'Общий объём')}:
                                <b>{currencyFormat} {feedTotalVolume}</b>
                            </span>
                        </div>
                        </>
                    )}
                </div>
            </section>

            {/* ── Модалка деталей транзакции */}
            {isModalOpen && selectedRowData && (
                <div
                    className={`${s.modalOverlay} ${isModalClosing ? s.modalOverlayClosing : ''}`}
                    onClick={handleCloseModal}
                >
                    <div
                        className={`${s.modal} ${isModalClosing ? s.modalClosing : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className={s.modalHead}>
                            <div className={s.modalBrand}>
                                {siteSettings?.siteLogo && (
                                    <img
                                        className={s.modalLogo}
                                        src={siteSettings.siteLogo.startsWith('/') || siteSettings.siteLogo.startsWith('http')
                                            ? siteSettings.siteLogo
                                            : `/logo/${encodeURIComponent(siteSettings.siteLogo)}`}
                                        alt=""
                                        onError={(e) => {
                                            if (!e.target.src.includes('/data/logo/') && siteSettings?.siteLogo) {
                                                e.target.src = `/data/logo/${encodeURIComponent(siteSettings.siteLogo)}`;
                                            } else {
                                                e.target.style.display = 'none';
                                            }
                                        }}
                                    />
                                )}
                                <span className={s.modalBrandText}>{siteSettings?.siteName || ''}</span>
                            </div>
                            <button type="button" className={s.modalClose} onClick={handleCloseModal} aria-label="Close">
                                <X size={17} strokeWidth={2.2} aria-hidden="true" />
                            </button>
                        </div>

                        {/* Чек: верх (маршрут + сумма) и низ (детали), между ними перфорация */}
                        <div className={s.receipt}>
                        <div className={s.routeBlock}>
                            <div className={s.route}>
                                <span className={s.routeNode}>
                                    {siteSettings?.siteLogo ? (
                                        <img
                                            src={siteSettings.siteLogo.startsWith('/') || siteSettings.siteLogo.startsWith('http')
                                                ? siteSettings.siteLogo
                                                : `/logo/${encodeURIComponent(siteSettings.siteLogo)}`}
                                            alt=""
                                            onError={(e) => {
                                                if (!e.target.src.includes('/data/logo/') && siteSettings?.siteLogo) {
                                                    e.target.src = `/data/logo/${encodeURIComponent(siteSettings.siteLogo)}`;
                                                } else {
                                                    e.target.style.display = 'none';
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Send size={17} strokeWidth={2} aria-hidden="true" />
                                    )}
                                </span>
                                <span className={s.routeLine} aria-hidden="true">
                                    <span className={s.routeArrow}>
                                        <ArrowRight size={11} strokeWidth={2.6} />
                                    </span>
                                </span>
                                <span className={`${s.routeNode} ${s.routeNodeBank}`}>
                                    {getBankIcon(selectedRowData.network, siteSettings) ? (
                                        <BankLogo src={getBankIcon(selectedRowData.network, siteSettings)} alt={selectedRowData.network} />
                                    ) : (
                                        <Landmark size={17} strokeWidth={2} aria-hidden="true" />
                                    )}
                                </span>
                            </div>

                            <span className={s.modalAmountLabel}>
                                {t('modals.success.amountSent', 'Сумма отправлена')}
                            </span>
                            <span className={s.modalAmountValue}>
                                <span className={s.modalCurrency}>{currencyFormat}</span>
                                {(() => {
                                    const [intPart, cents] = splitAmount(countedAmount || selectedRowData.amount);
                                    return (
                                        <span className={s.modalAmountNum}>
                                            {intPart}
                                            {cents !== null && <span className={s.modalCents}>{cents}</span>}
                                        </span>
                                    );
                                })()}
                            </span>

                            <div className={s.modalChips}>
                                <span className={`${s.txStatus} ${statusBadgeClass(selectedRowData.status)}`}>
                                    {translateStatus(selectedRowData.status)}
                                </span>
                                <span className={s.dateChip}>
                                    <CalendarDays size={11} strokeWidth={2} aria-hidden="true" />
                                    {formatModalDate(selectedRowData)}
                                </span>
                            </div>
                        </div>

                        {/* Перфорация отрывного чека: вырезы по бокам + пунктир */}
                        <div className={s.perforation} aria-hidden="true">
                            <i className={s.notchL} />
                            <span className={s.perfLine} />
                            <i className={s.notchR} />
                        </div>

                        {/* Таймлайн статуса: пройденные шаги зависят от статуса транзакции */}
                        {(() => {
                            const reached = timelineReached(selectedRowData.status);
                            const isDone = (i) => i < reached;
                            const isCurrent = (i) => i === reached && reached < 3;
                            return (
                                <div className={s.timeline}>
                                    {timelineSteps.map((label, i) => (
                                        <React.Fragment key={label}>
                                            {i > 0 && (
                                                <span className={`${s.tLine} ${i <= reached - 1 ? s.tLineDone : ''}`} />
                                            )}
                                            <div className={s.tStep}>
                                                <span className={`${s.tDot} ${isDone(i) ? s.tDotDone : ''} ${isCurrent(i) ? s.tDotCurrent : ''} ${isDone(i) && reached === 3 && i === 2 ? s.tDotFinal : ''}`}>
                                                    {isDone(i) && <Check size={10} strokeWidth={3} aria-hidden="true" />}
                                                </span>
                                                <span className={`${s.tLabel} ${isDone(i) ? s.tLabelDone : ''}`}>{label}</span>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            );
                        })()}

                        {/* Чековый список деталей */}
                        <div className={s.modalDetails}>
                            <span className={s.modalDetailsTitle}>
                                {t('modals.success.recipientDetails', 'Данные получателя')}
                            </span>
                            <div className={s.modalDetailRow}>
                                <span className={s.modalDetailLabel}>
                                    <span className={s.detailIconPlate}><Copy size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                    TX ID
                                </span>
                                <span className={s.modalDetailValue}>
                                    <code className={s.modalMono}>#{selectedRowData.txId}</code>
                                    <button
                                        type="button"
                                        className={s.eyeBtn}
                                        onClick={() => copyTxId(selectedRowData.txId)}
                                        aria-label={t('settings.address.copy', 'Копировать')}
                                    >
                                        {txIdCopied
                                            ? <Check size={13} strokeWidth={2.4} className={s.copyDone} aria-hidden="true" />
                                            : <Copy size={13} strokeWidth={2} aria-hidden="true" />}
                                    </button>
                                </span>
                            </div>
                            <div className={s.modalDetailRow}>
                                <span className={s.modalDetailLabel}>
                                    <span className={s.detailIconPlate}><User size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                    {t('modals.success.userDetails.name', 'Получатель:')}
                                </span>
                                <span className={s.modalDetailValue}>{selectedRowData.name}</span>
                            </div>
                            <div className={s.modalDetailRow}>
                                <span className={s.modalDetailLabel}>
                                    <span className={s.detailIconPlate}><Landmark size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                    {t('modals.success.userDetails.bank', 'Банк:')}
                                </span>
                                <span className={s.modalDetailValue}>
                                    {selectedRowData.network}
                                    {getBankIcon(selectedRowData.network, siteSettings) && (
                                        <span className={s.modalBankPlate}>
                                            <BankLogo src={getBankIcon(selectedRowData.network, siteSettings)} alt="" />
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className={s.modalDetailRow}>
                                <span className={s.modalDetailLabel}>
                                    <span className={s.detailIconPlate}><CreditCard size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                    {siteSettings?.bankAccountDisplayName || t('modals.success.userDetails.ibanBic', 'Счет:')}
                                </span>
                                <span className={s.modalDetailValue}>
                                    <code className={s.modalMono}>
                                        {isAccountVisible
                                            ? selectedRowData.cbucvu
                                            : `****${selectedRowData.cbucvu.slice(-4)}`}
                                    </code>
                                    <button
                                        type="button"
                                        className={s.eyeBtn}
                                        onClick={() => setIsAccountVisible(!isAccountVisible)}
                                        aria-label={isAccountVisible ? 'Hide' : 'Show'}
                                    >
                                        {isAccountVisible
                                            ? <Eye size={13} strokeWidth={2} aria-hidden="true" />
                                            : <EyeOff size={13} strokeWidth={2} aria-hidden="true" />}
                                    </button>
                                </span>
                            </div>
                        </div>
                        </div>

                        <button type="button" className={s.modalCloseBtn} onClick={handleCloseModal}>
                            {t('modals.success.close', 'Закрыть')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
